/**
 * Theme Initialization — applies saved theme CSS variables before first paint.
 *
 * Reads the user's saved theme from localStorage and sets --theme-primary-rgb
 * and --theme-secondary-rgb on document.documentElement. This must run as early
 * as possible (before React renders) to prevent a flash of default-theme colors.
 *
 * Exact port of the inline <script> in public/index.html (lines 19-45).
 * When React becomes the host, this replaces that script.
 * In hybrid mode, both run — the second write is a harmless no-op.
 */

const THEMES: Record<string, { p: string; s: string }> = {
  default: { p: '#0075d6', s: '#53b50a' },
  gold: { p: '#998800', s: '#1192A6' },
  green: { p: '#58B50A', s: '#0075d6' },
  red: { p: '#980000', s: '#CB4814' },
  regal: { p: '#4169e1', s: '#b69133' },
  bubblegum: { p: '#ec2072', s: '#7c20f6' },
  'high contrast': { p: '#222222', s: '#999999' },
};

function hexToRgbChannels(hex: string): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return `${parseInt(h.slice(0, 2), 16)} ${parseInt(h.slice(2, 4), 16)} ${parseInt(h.slice(4, 6), 16)}`;
}

export function initTheme(): void {
  try {
    const raw = localStorage.getItem('userSettings');
    if (!raw) return;
    const settings = JSON.parse(raw);
    const themeName = settings?.theme;
    if (!themeName) return;
    const colors = THEMES[themeName];
    if (!colors) return;
    document.documentElement.style.setProperty('--theme-primary-rgb', hexToRgbChannels(colors.p));
    document.documentElement.style.setProperty('--theme-secondary-rgb', hexToRgbChannels(colors.s));
  } catch {
    // Graceful fallback — CSS defaults in index.css apply
  }
}
